import localforage from 'localforage';

// Blog Post Utilities
export const fetchBlogPosts = async () => {
  try {
    console.log('Fetching blog posts');
    
    // Get blog posts directly from the store
    const blogPostsStore = localforage.createInstance({
      name: 'blogPosts'
    });
    
    const keys = await blogPostsStore.keys();
    console.log('Blog posts keys in store:', keys);
    
    if (keys.length === 0) {
      console.warn('No blog posts found in store');
      return [];
    }
    
    // Fetch all posts
    const allPosts = await Promise.all(
      keys.map(async key => {
        try {
          const post = await blogPostsStore.getItem(key);
          return post;
        } catch (err) {
          console.error(`Error fetching post with key ${key}:`, err);
          return null;
        }
      })
    );
    
    // Filter out invalid posts
    const validPosts = allPosts.filter(post => 
      post && 
      post.id && 
      post.title && 
      post.content
    );
    
    console.log(`Found ${validPosts.length} valid posts out of ${allPosts.length} total posts`);
    
    // Sort by date, newest first
    validPosts.sort((a, b) => {
      const dateA = new Date(a.publishedDate || a.createdAt);
      const dateB = new Date(b.publishedDate || b.createdAt);
      return dateB - dateA;
    });
    
    return validPosts;
  } catch (error) {
    console.error('Error fetching blog posts:', error);
    return [];
  }
};

export const getBlogPostById = async (id) => {
  try {
    console.log('Getting blog post with ID:', id);
    
    // Get the blog post from the store
    const blogPostsStore = localforage.createInstance({
      name: 'blogPosts'
    });
    
    // Log all keys in the store to debug
    const keys = await blogPostsStore.keys();
    console.log('Available blog post keys:', keys);
    
    // Check if the ID exists in the store
    if (!keys.includes(id)) {
      console.warn(`Blog post with ID ${id} not found in keys list`);
      
      // Try to find a post that might have this ID (case sensitivity, etc.)
      const allPosts = await Promise.all(keys.map(key => blogPostsStore.getItem(key)));
      const matchingPost = allPosts.find(post => 
        post && (post.id === id || post.slug === id)
      );
      
      if (matchingPost) {
        console.log('Found matching post by alternative lookup:', matchingPost.id);
        return matchingPost;
      }
      
      return null;
    }
    
    const post = await blogPostsStore.getItem(id);
    console.log('Retrieved post:', post);
    
    if (!post) {
      console.error(`Blog post with ID ${id} not found`);
      return null;
    }
    
    // Validate the post has required fields
    if (!post.title || !post.content) {
      console.error(`Blog post with ID ${id} has missing required fields`);
      return null;
    }
    
    return post;
  } catch (error) {
    console.error(`Error fetching blog post with ID ${id}:`, error);
    return null;
  }
};

export const createNewBlogPost = async () => {
  try {
    // TO DO: implement createBlogPost logic
  } catch (error) {
    console.error('Error creating blog post:', error);
    throw error;
  }
};

export const modifyBlogPost = async () => {
  try {
    // TO DO: implement updateBlogPost logic
  } catch (error) {
    console.error('Error updating blog post:', error);
    throw error;
  }
};

export const removeBlogPost = async () => {
  try {
    // TO DO: implement deleteBlogPost logic
  } catch (error) {
    console.error('Error deleting blog post:', error);
    throw error;
  }
};

// Authentication Utilities
export const login = async (username) => {
  try {
    if (!username) {
      throw new Error('Username is required');
    }
    // TO DO: implement authenticateUser logic
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

export const registerNewUser = async (username, email) => {
  try {
    // Input validation
    if (!username || !email) {
      throw new Error('Username and email are required');
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      throw new Error('Invalid email format');
    }

    // TO DO: implement registerUser logic
  } catch (error) {
    console.error('Registration error:', error);
    throw error;
  }
};

// Services Utility
export const fetchServices = async () => {
  try {
    // TO DO: implement getServices logic
  } catch (error) {
    console.error('Error fetching services:', error);
    return [];
  }
};

/**
 * Get all services from the store
 * @returns {Promise<Array>} Array of service objects
 */
export async function getAllServicesFromStore() {
  const servicesStore = localforage.createInstance({
    name: 'services'
  });
  try {
    const keys = await servicesStore.keys();
    const services = [];
    for (const key of keys) {
      const service = await servicesStore.getItem(key);
      if (service) {
        services.push(service);
      }
    }
    return services;
  } catch (error) {
    console.error('Error retrieving services:', error);
    return [];
  }
}
